import { createFileRoute } from "@tanstack/react-router";
import { Header, Footer } from "@/components/layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useState } from "react";
import { z } from "zod";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Mail } from "lucide-react";
import { buildMeta } from "@/lib/seo";
import { breadcrumbScript } from "@/lib/breadcrumbs";

export const Route = createFileRoute("/contact")({
  head: () => {
    const base = buildMeta({
      title: "Kontakt — napisz do zespołu | Tanie Latanie",
      description: "Skontaktuj się z zespołem Tanie Latanie. Pytania, sugestie i współpraca — odpowiadamy w 24h.",
      path: "/contact",
    });
    return {
      ...base,
      scripts: [
        
        breadcrumbScript([
          { name: "Strona główna", path: "/" },
          { name: "Kontakt", path: "/contact" },
        ]),
      ],
    };
  },
  component: ContactPage,
});

const schema = z.object({
  name: z.string().trim().min(2, "Podaj imię").max(100),
  email: z.string().trim().email("Nieprawidłowy email").max(255),
  message: z.string().trim().min(10, "Min. 10 znaków").max(2000),
});

function ContactPage() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const parsed = schema.safeParse(form);
    if (!parsed.success) {
      toast.error(parsed.error.issues[0].message);
      return;
    }
    setLoading(true);
    const { error } = await supabase.from("contact_messages").insert(parsed.data);
    setLoading(false);
    if (error) {
      toast.error("Nie udało się wysłać. Spróbuj ponownie.");
      return;
    }
    toast.success("Wiadomość wysłana — odpowiemy najszybciej jak to możliwe.");
    setForm({ name: "", email: "", message: "" });
  };

  return (
    <div className="min-h-screen">
      <Header />
      <section className="container mx-auto max-w-2xl px-4 py-16">
        <div className="mb-6 inline-flex h-16 w-16 items-center justify-center rounded-lg bg-gradient-sunset shadow-glow">
          <Mail className="h-8 w-8 text-primary-foreground" />
        </div>
        <h1 className="font-display text-4xl font-bold md:text-5xl">Napisz do nas</h1>
        <p className="mt-3 text-muted-foreground">
          Masz pytanie, sugestię lub problem z rezerwacją? Wyślij wiadomość — odpowiemy mailem.
        </p>

        <form onSubmit={submit} className="mt-8 space-y-4 glass rounded-xl p-6">
          <div className="space-y-2">
            <Label htmlFor="name">Imię</Label>
            <Input id="name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required maxLength={100} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input id="email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required maxLength={255} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="message">Wiadomość</Label>
            <Textarea id="message" rows={6} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} required maxLength={2000} />
          </div>
          <Button type="submit" variant="hero" size="lg" disabled={loading} className="w-full">
            {loading ? "Wysyłanie..." : "Wyślij wiadomość"}
          </Button>
        </form>
      </section>
      <Footer />
    </div>
  );
}
